import { Stack } from '@chakra-ui/react'
import { InputFormControl, MultipleSelectFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import type { EditOrganizationUnit } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import { forwardRef, useEffect } from 'react'

import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { useAuth } from '@/context/AuthContext'
import { useAvailableProducts } from '@/hooks/useProducts'
import { Insurer, Product } from 'BrokerTsLib/src/enums'
import { useGetAvailableInsurers, useGetOrganizationFleetNumbers } from 'BrokerTsLib/src/hooks/useOrganizationUnit'

export const OrganizationVehicleLeasingInsuranceFormSection = forwardRef(
  function OrganizationVehicleLeasingInsuranceFormSection(props: FormSectionProps, ref: Ref<HTMLDivElement>) {
    const { control, watch, setValue } = useFormContext<EditOrganizationUnit>()

    const ouId = watch('id')
    const superiorOuId = watch('superiorOrganizationUnitId')
    const { data } = useGetAvailableInsurers(ouId ?? superiorOuId)
    const isGcpAllowed = data?.data?.some(insurer => insurer.insurerId === Insurer.GeneraliCeskaPojistovna)

    const { data: fleetNumbersData } = useGetOrganizationFleetNumbers(ouId)
    const fleetNumbers = fleetNumbersData?.data

    // předvyplnění aktuálně přiřazených flotilových čísel po načtení
    useEffect(() => {
      if (fleetNumbers)
        setValue('fleetNumberIds', fleetNumbers.filter(fn => fn.selected).map(fn => fn.id))
    }, [fleetNumbers, setValue])

    const isGcpHomeServiceAllowed = watch('properties.allowGcpHomeService')

    const { user: authUser } = useAuth()
    const products = useAvailableProducts()

    return <FormSection ref={ref} sectionTitle='Nastavení pojištění vozidel' {...props}>
      <Stack gap={6}>
        {isGcpAllowed && <>
          <SwitchFormControl name='properties.allowGcpHomeService' control={control} label="Povolit Domovský servis GČP" />
          {isGcpHomeServiceAllowed &&
            <InputFormControl name='properties.gcpHomeServiceIds' control={control} label="ID Domovského servisu GČP"
              helperText="Zadávejte kód z číselníku GČP ve formátu <HS_ID>|<HS_SEQ_NO>"
            />
          }
        </>
        }
        
        {(authUser?.allowGenerateKoopSalon && products.every(x => x === Product.VehicleLeasingInsurance)) &&
          <SwitchFormControl name='properties.generateKoopSalonOffers' control={control} label="Generovat nabídky KOOPsalon" />
        }

        {(fleetNumbers?.length ?? 0) > 0 &&
          <MultipleSelectFormControl
            name='fleetNumberIds'
            control={control}
            label='Flotilová čísla'
            helperText='Přiřazená čísla mají pro tuto organizaci přednost před výchozími čísly z nastavení pojišťovny.'
            options={fleetNumbers!.map(fn => ({
              value: fn.id,
              label: `${fn.insurerName ?? ''} — ${fn.number}${fn.name ? ` (${fn.name})` : ''} — ${fn.fixedPrice ? 'fix' : 'float'}${fn.leasingTypeName ? `, ${fn.leasingTypeName}` : ''}`
            }))}
            valueAsNumber
          />
        }
      </Stack>
    </FormSection>
  }
)